// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Master grid columns (Stage 6B.3).
//
// Flat single-row layout, server-mode. Mirrors customer columns shape
// but trims to the supplier essentials (no wastage, no parent map, no
// price group, no email column). Hide-by-default for Code / PAN /
// Legal Name / Trade Name / GST State Code via `hidden: true`.
// ─────────────────────────────────────────────────────────────────

import { Tag } from 'antd';
import { formatINR } from '../../../../components/common/erp-grid';
import { STATUS_COLOR, GST_REGISTRATION_TYPE_OPTIONS } from './supplierConstants';

const REG_LABEL = Object.fromEntries(
  GST_REGISTRATION_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

export function buildSupplierColumns({ currentPage, pageSize } = {}) {
  const sNo = {
    title: '#',
    key: '__sno__',
    width: 56,
    align: 'center',
    fixed: 'left',
    render: (_v, _r, idx) => {
      const cp = Number(currentPage) > 0 ? Number(currentPage) : 1;
      const ps = Number(pageSize)    > 0 ? Number(pageSize)    : 25;
      return <span className="erp-grid__muted">{(cp - 1) * ps + idx + 1}</span>;
    }
  };

  const supplierCode = {
    title: 'Code',
    key: 'supplierCode',
    dataIndex: 'supplierCode',
    sortField: 'supplier_code',
    sorter: true,
    width: 110,
    dataType: 'string',
    hidden: true,
    render: (v) => v
      ? <code className="erp-grid__code">{v}</code>
      : <span className="erp-grid__muted">—</span>
  };

  const supplierName = {
    title: 'Supplier Name',
    key: 'supplierName',
    dataIndex: 'supplierName',
    sortField: 'supplier_name',
    sorter: true,
    width: 260,
    fixed: 'left',
    ellipsis: true,
    dataType: 'string',
    render: (v) => <span className="erp-grid__primary">{v}</span>
  };

  const gstinOrUrp = {
    title: 'GSTIN / URP',
    key: 'gstin',
    dataIndex: 'gstin',
    sortField: 'gstin',
    sorter: true,
    width: 170,
    dataType: 'string',
    render: (v, r) => {
      if (v) return <code className="erp-grid__code">{v}</code>;
      if (r && r.gstRegistrationType === 'unregistered') {
        return <Tag color="default" className="erp-grid__tag">URP</Tag>;
      }
      return <span className="erp-grid__muted">—</span>;
    }
  };

  const pan = {
    title: 'PAN',
    key: 'pan',
    dataIndex: 'pan',
    width: 130,
    dataType: 'string',
    hidden: true,
    render: (v) => v
      ? <code className="erp-grid__code">{v}</code>
      : <span className="erp-grid__muted">—</span>
  };

  const legalName = {
    title: 'Legal Name',
    key: 'gstLegalName',
    dataIndex: 'gstLegalName',
    width: 220,
    dataType: 'string',
    hidden: true,
    ellipsis: true
  };
  const tradeName = {
    title: 'Trade Name',
    key: 'gstTradeName',
    dataIndex: 'gstTradeName',
    width: 220,
    dataType: 'string',
    hidden: true,
    ellipsis: true
  };

  const mobile = {
    title: 'Mobile',
    key: 'mobile',
    dataIndex: 'mobile',
    width: 130,
    dataType: 'string',
    render: (v) => v || <span className="erp-grid__muted">—</span>
  };

  const city = {
    title: 'City',
    key: 'city',
    dataIndex: 'city',
    sortField: 'city',
    width: 140,
    dataType: 'string',
    render: (v) => v || <span className="erp-grid__muted">—</span>
  };

  const gstStateCode = {
    title: 'State Code',
    key: 'gstStateCode',
    dataIndex: 'gstStateCode',
    width: 90,
    dataType: 'string',
    hidden: true
  };

  // Legacy free-text "Supplier Category". Stage 6B.3B introduced the
  // structured supplierGroupName column (below); this is kept for
  // continuity until a future stage decides whether to retire it.
  const supplierCategory = {
    title: 'Category',
    key: 'supplierCategory',
    dataIndex: 'supplierCategory',
    sortField: 'supplier_category',
    sorter: true,
    width: 140,
    dataType: 'string',
    hidden: true,
    groupable: true,
    groupField: 'supplier_category',
    render: (v) => v || <span className="erp-grid__muted">—</span>
  };

  // Stage 6B.3B — structured Supplier Group column (JOIN sg.group_name).
  const supplierGroupName = {
    title: 'Supplier Group',
    key: 'supplierGroupName',
    dataIndex: 'supplierGroupName',
    sortField: 'supplier_group_name',
    sorter: true,
    width: 200,
    dataType: 'string',
    groupable: true,
    groupField: 'supplier_group_name',
    render: (v, r) => v
      ? (
        <span>
          {v}
          {r && r.supplierGroupCode
            ? <code className="erp-grid__muted" style={{ marginInlineStart: 6, fontSize: 11 }}>
                {r.supplierGroupCode}
              </code>
            : null}
        </span>
      )
      : <span className="erp-grid__muted">—</span>
  };

  const gstType = {
    title: 'GST Type',
    key: 'gstRegistrationType',
    dataIndex: 'gstRegistrationType',
    sortField: 'gst_registration_type',
    sorter: true,
    width: 160,
    dataType: 'enum',
    groupable: true,
    groupField: 'gst_registration_type',
    filters: GST_REGISTRATION_TYPE_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
    filterField: 'gst_registration_type',
    render: (v) => v
      ? <Tag color="blue" className="erp-grid__tag">{REG_LABEL[v] || v}</Tag>
      : <span className="erp-grid__muted">—</span>
  };

  const msme = {
    title: 'MSME',
    key: 'msmeRegistered',
    dataIndex: 'msmeRegistered',
    sortField: 'msme_registered',
    width: 80,
    align: 'center',
    dataType: 'boolean',
    filters: [{ text: 'Yes', value: true }, { text: 'No', value: false }],
    filterField: 'msme_registered',
    render: (v) => v === true
      ? <Tag color="purple" className="erp-grid__tag">MSME</Tag>
      : <span className="erp-grid__muted">—</span>
  };

  const creditDays = {
    title: 'Credit Days',
    key: 'creditDays',
    dataIndex: 'creditDays',
    sortField: 'credit_days',
    sorter: true,
    width: 110,
    align: 'right',
    dataType: 'number',
    editable: true,
    render: (v) => Number.isFinite(Number(v)) ? Number(v) : 0
  };

  const openingBalance = {
    title: 'Opening ( ₹ )',
    key: 'openingBalance',
    dataIndex: 'openingBalance',
    sortField: 'opening_balance',
    sorter: true,
    width: 130,
    align: 'right',
    dataType: 'number',
    render: (v, r) => {
      const n = Number(v) || 0;
      if (n === 0) return <span className="erp-grid__muted">—</span>;
      const type = r && r.openingBalanceType;
      return (
        <span>
          {formatINR(n)}
          {type ? <span className="erp-grid__muted" style={{ marginLeft: 4 }}>{type[0].toUpperCase()}</span> : null}
        </span>
      );
    }
  };

  const status = {
    title: 'Status',
    key: 'status',
    dataIndex: 'status',
    sortField: 'status',
    sorter: true,
    width: 96,
    align: 'center',
    dataType: 'enum',
    groupable: true,
    groupField: 'status',
    filters: [
      { text: 'Active',   value: 'active'   },
      { text: 'Inactive', value: 'inactive' },
      { text: 'Blocked',  value: 'blocked'  }
    ],
    filterField: 'status',
    render: (v) => (
      <Tag color={STATUS_COLOR[v] || 'default'} className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>
        {(v || '').toUpperCase()}
      </Tag>
    )
  };

  return [
    sNo, supplierCode, supplierName,
    supplierGroupName,
    gstinOrUrp, pan, legalName, tradeName,
    mobile, city, gstStateCode,
    supplierCategory, gstType, msme,
    creditDays, openingBalance, status
  ];
}
